import { useUserProfileStore } from '@/stores/platform/accounts/UserProfileStore';
import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';

const routes: RouteRecordRaw[] = [
  {
    path: '/test',
    name: 'CrystalGrantTest',
    component: () => import('@/pages/CrystalGrantTest.vue'),
  },
  {
    path: '/',
    name: 'Landing',
    component: () => import('@/pages/Landing.vue'),
  },
  {
    path: '/intro',
    name: 'Intro',
    component: () => import('@/pages/Intro.vue'),
    meta: {
      intro: true,
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/Login.vue'),
  },
  {
    path: '/magic-link/:appId/:code/:email(.*)',
    name: 'MagicLink',
    component: () => import('@/pages/Login.vue'),
  },
  {
    path: '/library',
    name: 'Library',
    component: () => import('@/pages/Library.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/book/:code',
    name: 'BookOverview',
    component: () => import('@/pages/BookOverview.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/book/:code/:chapter',
    name: 'BookChapter',
    component: () => import('@/pages/BookChapter.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/book/:code/:chapter/questions/:question',
    name: 'BookChapterQuestions',
    component: () => import('@/pages/BookChapterQuestions.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/book/:code/:chapter/questions/:question/:answer',
    name: 'BookChapterQuestionsAnswer',
    component: () => import('@/pages/BookChapterQuestionsAnswer.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/Error404.vue'),
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

function hasWatchedIntro(): boolean {
  try {
    return localStorage.getItem('watched-intro') === 'true';
  } catch {
    return false;
  }
}

router.beforeEach((to) => {
  const userStore = useUserProfileStore();
  const isLoggedIn = userStore.hasSession;

  // If you've watched the intro already, skip straight to login
  if (to.meta.intro && hasWatchedIntro()) {
    return { name: 'Login' };
  }

  // If the user hits Login (but NOT MagicLink) while already logged in, bounce to Library.
  // MagicLink must always pass through so the new credentials can be processed.
  if (to.name === 'Login' && isLoggedIn) {
    return { name: 'Library' };
  }

  // Public routes — no auth needed
  if (!to.meta.requiresAuth) return true;

  // Auth routes — check for session (synchronous, no network I/O).
  // Pages handle their own API errors with loading/error states.
  if (isLoggedIn) return true;

  return { name: 'Login' };
});
